import {useCallback, useEffect} from 'react';

import {HTTP_METHODS} from '@nfq/typed-next-api';

import {useClickTracking} from 'Application/useCases/useTracking';

/**
 * Custom hook that tracks link clicks throughout the document and logs them using the `trackLinkClick` function.
 * This hook attaches a click event listener to the document and extracts the URL of any clicked anchor (`<a>`) elements.
 * It then triggers the `trackLinkClick` function, logging the click with details such as whether the link is external or internal.
 * The listener is removed when the component is unmounted to prevent memory leaks.
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   useLinkTracking();
 *
 *   return (
 *     <div>
 *       <a href="/internal-page">Internal Link</a>
 *       <a href="https://external.com">External Link</a>
 *     </div>
 *   );
 * };
 * ```
 */
export const useLinkTracking = () => {
    const {trackLinkClick} = useClickTracking();

    /**
     * Handles click events on the document and tracks link clicks.
     * Extracts the URL from the clicked element and triggers the `trackLinkClick` function if a valid URL is found.
     * It checks if the clicked element is an `<a>` tag or has an `<a>` tag as its parent.
     *
     * @param event The mouse event triggered by clicking on the document.
     */
    const trackClick = useCallback((event: MouseEvent) => {
        if (!event.target) {
            return;
        }

        const target = event.target as HTMLElement;
        const {tagName} = target;

        let url: string | null = '';

        if (tagName === 'A') {
            url = target.getAttribute('href');
        }

        const {parentElement} = target;

        if (!url && parentElement?.tagName === 'A') {
            url = parentElement.getAttribute('href');
        }

        if (url) {
            void trackLinkClick({
                body: {
                    external: !url.startsWith('/'),
                    url
                },
                method: HTTP_METHODS.POST
            });
        }
    }, [trackLinkClick]);

    useEffect(() => {
        document.documentElement.addEventListener('click', trackClick);

        return () => {
            document.documentElement.removeEventListener('click', trackClick);
        };
    }, [trackClick]);
};